import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { tokenStore } from "../lib/api";

const AuthContext = createContext(null);

const REGION_CURRENCY = { US: "USD", IN: "INR", GB: "GBP", EU: "EUR", AE: "AED", GLOBAL: "USD" };

function guessRegion() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("Kolkata") || tz.includes("Calcutta")) return "IN";
    if (tz.includes("Dubai")) return "AE";
    if (tz.includes("London")) return "GB";
    if (tz.startsWith("Europe/")) return "EU";
    if (tz.startsWith("America/")) return "US";
    const loc = (navigator.language || "").toUpperCase();
    const cc = loc.split("-")[1];
    if (["IN", "US", "GB", "AE"].includes(cc)) return cc;
    if (["DE", "FR", "ES", "IT", "NL", "IE", "PT", "AT", "BE", "FI", "GR"].includes(cc)) return "EU";
  } catch (e) {}
  return "GLOBAL";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [coins, setCoins] = useState(0);
  const [region, setRegion] = useState(() => localStorage.getItem("at_region") || "");
  const [currency, setCurrency] = useState(() => localStorage.getItem("at_currency") || "USD");
  const [regions, setRegions] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const applyRegion = useCallback((r, persist = true, syncServer = false) => {
    const cur = REGION_CURRENCY[r] || "USD";
    setRegion(r);
    setCurrency(cur);
    if (persist) {
      localStorage.setItem("at_region", r);
      localStorage.setItem("at_currency", cur);
    }
    if (syncServer && tokenStore.access) {
      api.put("/me/region", { region: r }).catch(() => {});
    }
  }, []);

  const loadMe = useCallback(async () => {
    if (!tokenStore.access) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setCoins(data.coin_balance || 0);
      if (!localStorage.getItem("at_region") && data.region) applyRegion(data.region, true, false);
    } catch (e) {
      tokenStore.clear();
      setUser(false);
    }
  }, [applyRegion]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // Load currency metadata + auto-detect region on first visit
  useEffect(() => {
    api.get("/currencies").then((r) => {
      setCurrencies(r.data.currencies);
      setRegions(r.data.regions);
    }).catch(() => {});
    if (!localStorage.getItem("at_region")) {
      api.get("/geo")
        .then((r) => {
          const detected = r.data?.region && r.data.region !== "GLOBAL" ? r.data.region : guessRegion();
          applyRegion(detected, true, false);
        })
        .catch(() => applyRegion(guessRegion(), true, false));
    }
  }, [applyRegion]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    tokenStore.set(data);
    setUser(data.user);
    await loadMe();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    tokenStore.set(data);
    setUser(data.user);
    await loadMe();
    return data.user;
  };

  const logout = () => {
    tokenStore.clear();
    setUser(false);
    setCoins(0);
  };

  const refreshCoins = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet");
      setCoins(data.coin_balance);
    } catch (e) {}
  }, []);

  const setRegionCurrency = (r) => applyRegion(r, true, true);

  return (
    <AuthContext.Provider
      value={{ user, setUser, coins, setCoins, login, register, logout, refreshCoins, loadMe,
               region: region || "GLOBAL", currency, regions, currencies, setRegionCurrency }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
